import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import CustomButton from '@/components/Common/Button';
import { useNavigate } from "react-router-dom";

import { store, useAppSelector } from '@/app/store';
import { numericSort, padZeros, RoleContext } from '@/app/api/common';
import { checkNoError, getUrl, getField } from '@/features/channels/channelsSlice';
import { copyProjectInfo, getPlatformDeviceCount, getProjectById, HotelInfo } from '@/features/projects/projectsSlice';
import { isLightRow } from '@/features/projects/mwLight';

import FlexRow from '@/components/Common/FlexRow';
import Grid from '@/components/Common/Grid';
import Input from '@/components/Common/Input';
import MenuItem from '@/components/Common/MenuItem';
import { DeleteModal, Modal as CommonModal } from '@/components/Common/Modals';

export interface NumberTextProps {
  children?:			React.ReactNode;
}

const NumberText = (props: NumberTextProps) => {
  return (
  <span className="text-[#1B69AA]">
    { props.children }
  </span>
  );
};

export interface ProjectRow {
  id:					string;
  name:					string;
  used_limit?:			string;
  rooms_max:     string;
  rooms_active:  string;
  rooms_live:    string;
  location:				string;
  bexio_url:			string;
  partner:			string;
  chainmanager:		string;
  manager:			string;
  contact:				string;
  tags:					string;
  state:				string;
  device_count?:		string;
  // §2.3 — an MW light row: no stats, no options, and the click opens
  // /mw_light/:id instead of the project page.
  mw_light:				boolean;
  requestDelete:		any;
  requestClone:			any;
}

export interface ProjectsProps {
  hideContainer?: boolean;  // e.g., 'channelList', 'main', etc.
  csvExport?: number;
  csvFile?: string;
  // When set (platform drill-down), append a column with the number of
  // devices of that platform each project has (stats.rooms.platforms cumul).
  platformFilter?: string;
}
const Projects = (props: ProjectsProps) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const projects = useAppSelector((state) => state.projects);
  const tags = useAppSelector((state) => state.tags.tags);
  const role = React.useContext(RoleContext);
  
  const [cols, setCols] = React.useState([] as any[]);
  const [rows, setRows] = React.useState([] as ProjectRow[]);
  
  const [deleteProject, setDeleteProject] = React.useState(null as HotelInfo | null);
  const [cloneProject, setCloneProject] = React.useState(null as HotelInfo | null);
  const [cloneName, setCloneName] = React.useState("");
  const [cloneTried, setCloneTried] = React.useState(false);
  const [csvExport, setCsvExport] = React.useState(0);
  const requestDelete = React.useRef((id: string) => ("" as any));
  const requestClone = React.useRef((id: string) => ("" as any));

  React.useEffect(() => {
    if (!props.hideContainer) {
      store.clearChannelListProjects();
    }
  }, [props.hideContainer]);

  React.useEffect(() => {
    if (role.admin || role?.privilages?.includes("ViewTags")) {
      store.getTags();
    }
    store.getProjects();
  }, []);

  React.useEffect(() => {
    const toolTip = (props: any): string => {
      const ret =	props.value
		  			?	props.value.toString()
		  			:	"";
      
      //console.log("Projects/useEffect/toolTip/ret=" + ret + "/props=", props);
      
      return ret;
    };
    
    const rws: ProjectRow[] = [];
    setCols([
    {
        field:			"id",
        headerName:		t("projects:grid:id"),
        comparator:		numericSort,
        sort:			"asc",
        flex:			0,
        minWidth:		80,
        maxWidth:		120,
        pinned:			'left',
        lockPinned:		true,
        cellStyle:		{ backgroundColor: '#ffffff' },
    },
    {
        field:			"name",
        headerName:		t("projects:grid:name"),
        flex:			1,
        minWidth:		200,
        //maxWidth:		300,
        cellRenderer:	NameCell,
    },
    {
        field:			"rooms_max",
        headerName:		t("projects:grid:rooms_max"),
        minWidth:		10,
        maxWidth: 100
    },
    {
        field:			"rooms_active",
        headerName:		t("projects:used"),
        flex:			0,
        minWidth:		10,
        maxWidth: 100
    },
    {
        field:			"rooms_live",
        headerName:		t("projects:grid:rooms_live"),
        minWidth:		10,
        maxWidth: 100
    },
    ...(
      props.platformFilter
      ? [{
          field:			"device_count",
          headerName:		t("projects:platforms:devices"),
          comparator:		numericSort,
          flex:			0,
          minWidth:		10,
          maxWidth: 120
        }]
      : []
    ),
        /*{
        field:			"location",
        headerName:		t("projects:grid:location"),
        flex:			0,
        minWidth:		100,
        maxWidth:		150,
    },*/
    ...(
     	role.admin
		?	[{
				field:			"bexio_url",
				headerName:		t("projects:grid:bexio_url"),
				flex:			0,
				minWidth:		100,
				maxWidth:		140,
				tooltipValueGetter:		toolTip,
			}]
		:	[]
    ),
    ...(
      role.admin || role?.privilages?.includes("SeeAllProjects")
      ? [{
          field:			"partner",
          headerName:		t("projects:grid:partner"),
          flex:			0,
          minWidth:		100,
          maxWidth:		150,
        },
        {
          field:			"chainmanager",
          headerName:		t("projects:grid:chainmanager"),
          flex:			0,
          minWidth:		100,
          maxWidth:		150,
        },
        {
          field:			"manager",
          headerName:		t("projects:grid:manager"),
          flex:			0,
          minWidth:		100,
          maxWidth:		150,
        }]
      : []
    ),
    ...(
      role.admin
      ? [{
          field:			"tags",
          headerName:		t("tags:tags"),
          flex:			1,
          minWidth:		150,
        }]
      : []
    ),
    /*{
        field:			"contact",
        headerName:		t("projects:grid:contact"),
        flex:			0,
        minWidth:		150,
        maxWidth:		200,
    },*/
    ...(
      !props.hideContainer && (role.admin || role?.privilages?.includes("EditProjectSettings") || role?.privilages?.includes("DeleteProjects"))
      ? [{
          field:			"options",
          headerName:		t("projects:grid:options"),
          flex:			0,
          filter:			false,
          minWidth:		90,
          maxWidth:		90,
          cellRenderer:	OptionsCell,
        }]
      : []
    ),
    ]);
    // When hideContainer is true (searchproject context), only show channelListProjects, don't fall back to all projects
    const projectsList = props.hideContainer
      ? (projects.channelListProjects || [])
      : (projects.channelListProjects && projects.channelListProjects.length > 0
          ? projects.channelListProjects
          : projects.projects);
    // Build a set of valid tag names from the global tags list
    const validTags = new Set(tags?.map((t: any) => t.tag) || []);

    for (let proj of projectsList) {
      // Map project tags to their tag names, filtering out deleted tags
      const tagDescriptions = proj.tags && proj.tags.length > 0
        ? proj.tags
            .map((t: any) => t.tag || '')
            .filter((tag: string) => tag !== '' && validTags.has(tag))
            .join(', ')
        : '';

      // §3.1 — a light row carries `stats: null` and `urls: []`, so every
      // figure and every executive cell is deliberately blank rather than a
      // row of zeroes that would read as a hotel with no rooms.
      const light = isLightRow(proj);

      const row: ProjectRow = {
      	id:					proj.id,
		name:				proj.name,
    rooms_max: proj.stats ? padZeros(proj.stats.rooms.allocated, 3):"" ,
    rooms_active:  proj.stats ? padZeros(proj.stats.rooms.created, 3): " " ,
    rooms_live: proj.stats ? padZeros(proj.stats.rooms.connected, 3) : '',
		location:			getField(proj.urls, "location"),
		bexio_url:			getField(proj.urls, "bexio_url"),
		partner:			proj.stats?.executives?.partner?.join(", ") || "",
		chainmanager:		proj.stats?.executives?.chain_manager?.join(", ") || "",
		manager:			proj.stats?.executives?.manager?.join(", ") || "",
		contact:			getField(proj.urls, "contact"),
		tags:				tagDescriptions,
		state:				proj.state || "enabled",
		device_count:		props.platformFilter
							?	padZeros(getPlatformDeviceCount(proj, props.platformFilter), 3)
							:	undefined,
		mw_light:			light,
		requestDelete:		requestDelete,
		requestClone:		requestClone,
      };

      rws.push(row);
    }

    setRows(rws);
  }, [projects.projects, projects.channelListProjects, t, role, props.hideContainer, props.platformFilter, tags]);
  
  const handleCellClick = (evt: any) => {
    console.log("Projects/handleCellClick/evt=", evt);
    if (evt.colDef.field == "options")
      return;

    const id = encodeURIComponent(evt?.data?.id);

    // §2.3 — an MW light row NEVER opens the project page: that read answers
    // 403 for this user.
    navigate(	evt?.data?.mw_light
    			?	`/mw_light/${id}`
    			:	`/projects/${id}`
    		);
  };
  
  const processDelete = async (obj: Object, answer: string) => {
    setDeleteProject(null);
    
    if (answer !== "yes") {
      return;
    }
    
    if (!deleteProject)
      return;
    
    const proj = getProjectById(deleteProject.id, projects.projects);
    if (!proj)
      return;
    
    let project = copyProjectInfo(proj);
    if (!project)
      return;
    
    project.to_delete = true;
    const mess = await store.setProject(project);
    console.log("Projects/processDelete/mess=", mess);
    
    if (!checkNoError(mess))
      return;
  };
  
  requestDelete.current = React.useCallback((id: string) => {
    const proj = getProjectById(id, projects.projects);
    setDeleteProject(proj);
  }, [projects.projects, setDeleteProject]);

  requestClone.current = React.useCallback((id: string) => {
    const proj = getProjectById(id, projects.projects);
    setCloneProject(proj);
    setCloneName(proj ? proj.name + " (Copy)" : "");
    setCloneTried(false);
  }, [projects.projects]);

  const processClone = async () => {
    if (!cloneProject)
      return;

    if (!cloneName || cloneName.trim() === "") {
      setCloneTried(true);
      return;
    }

    const mess = await store.cloneProject(cloneProject.id, cloneName.trim());
    if (!checkNoError(mess))
      return;

    setCloneProject(null);
    setCloneName("");
    setCloneTried(false);
  };
  
  return (
  <React.Fragment>
    {!props.hideContainer &&
    <FlexRow direction="column"
	         align="start"
	         className="pt-[32px] px-[32px] bg-white sticky top-[64px] z-10">
      <div className="text-[32px]/[48px]">
        { t('projects:caption') }
      </div>
      <div>
        <NumberText>
          { projects.projects.length }
        </NumberText>
        { ' ' +
          t('projects:projects') +
          ' - '
        }
        <NumberText>
          { projects.stats.allocated }
        </NumberText>
        { ' ' +
          t('projects:units') +
          ' - '
        }
        <NumberText>
          { projects.stats.created}
        </NumberText>
        { ' ' +
          t('projects:used')+
          ' - '
        }
        <NumberText>
          { projects.stats.connected }
        </NumberText>
        { ' ' +
          t('projects:connected') 
        }
      </div>
      <Divider className="my-[8px]" />
    </FlexRow>
    }
    <FlexRow direction="column"
             align="start"
             className="py-[16px] px-[32px]">
      <FlexRow className="w-full justify-between mb-[16px]">
        {((role.admin && !props.hideContainer) ||(!props.hideContainer && role?.privilages?.includes("CreateProjects"))) &&
          <Button className="bg-[#1B69AA] font-bold rounded-[5px] px-2"
                  onClick={() => navigate('/projects/__new__')}>
            <FlexRow justify="center" className="gap-[4px]">
              <div className="material-icons !text-white !text-[16px]">
                add
              </div>
              <div className="m14 text-white leading-normal">
                { t('projects:new') }
              </div>
            </FlexRow>
          </Button>
        }
        { !props.hideContainer && rows && rows.length > 0 &&
          <Button className="bg-[#1B69AA] font-bold rounded-[5px] px-2"
                  onClick={() => setCsvExport(csvExport + 1)}>
            <FlexRow justify="center" className="gap-[4px]">
              <div className="material-icons !text-white !text-[16px]">
                share
              </div>
              <div className="m14 text-white leading-normal">
                { t('channel_lists:options:export') }
              </div>
            </FlexRow>
          </Button>
        }
      </FlexRow>
      <Grid rowData={rows}
            columnDefs={cols}
            filter={true}
            showPages={true}
            pageSize={10}
            maxHeight={props.hideContainer ? undefined : "calc(100vh - 280px)"}
            onCellClicked={handleCellClick}
            csvFile={props.csvFile || 'projects.csv'}
            csvExport={props.csvExport || csvExport} />
    </FlexRow>
    {deleteProject  &&
      <DeleteModal title={t('projects:delete_project:title')}
                   message={t('projects:delete_project:message_p', {p: deleteProject.id, name: deleteProject.name})}
                   onResult={processDelete}>
        <>
        </>
      </DeleteModal>
    }
    {cloneProject &&
      <CommonModal title={t('projects:clone_project_modal:title')}
                   message={t('projects:clone_project_modal:message', {name: cloneProject.name, id: cloneProject.id})}
                   onResult={() => { setCloneProject(null); setCloneName(""); setCloneTried(false); }}>
        <FlexRow direction="column" align="start" className="gap-[16px] pb-[16px]">
          <Input label={t('projects:clone_project_modal:name')}
                 placeholder={t('projects:clone_project_modal:name_placeholder')}
                 isInvalid={cloneTried && (!cloneName || cloneName.trim() === "")}
                 errorMessage={t('projects:clone_project_modal:name_invalid')}
                 value={cloneName}
                 onValueChange={(v: string) => setCloneName(v)} />
        </FlexRow>
        <FlexRow justify="end" className="gap-[16px]">
          <CustomButton caption={t('modals:warning:cancel')}
                  onClick={() => { setCloneProject(null); setCloneName(""); setCloneTried(false); }}
                  layout="white" />
          <CustomButton caption={t('projects:clone_project_modal:clone')}
                  icon="content_copy"
                  onClick={() => processClone()} />
        </FlexRow>
      </CommonModal>
    }
  </React.Fragment>
  );
};

// The project name, with the "MW light" badge on a light row (§2.3). The badge
// is what tells a user who is a Manager elsewhere that THIS hotel opens a room
// list with a Checkout button and nothing else.
const NameCell = (props: any) => {
  const { t } = useTranslation();

  const name =	props.value
				?	props.value.toString()
				:	"";

  if (!props.data?.mw_light)
    return name;

  return (
  <FlexRow justify="start"
           className="h-full gap-[8px]">
    <span className="truncate">
      { name }
    </span>
    <span className="shrink-0 rounded-full bg-[#E8F0F8] text-[#1B69AA] text-[11px] font-medium px-[8px] py-[2px] whitespace-nowrap">
      { t('mw_light:badge') }
    </span>
  </FlexRow>
  );
};

const OptionsCell = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = React.useContext(RoleContext);

  const [open, setOpen] = React.useState(false);

  // §2.3 — no edit, no delete, no clone on an MW light row: the caller has no
  // rights on that project beyond checking rooms out.
  if (props.data?.mw_light)
    return null;
  
  const handleEdit = () => {
    setOpen(false);
    navigate(`/projects/${encodeURIComponent(props.data.id)}`)
  };
  
  const handleDelete = () => {
    setOpen(false);
    props.data.requestDelete.current(props.data.id);
  };

  const handleClone = () => {
    setOpen(false);
    props.data.requestClone.current(props.data.id);
  };

  const handleToggleState = async () => {
    setOpen(false);
    const proj = getProjectById(props.data.id, store.store.getState().projects.projects);
    if (!proj) return;
    const project = copyProjectInfo(proj);
    project.state = (props.data.state === "disabled") ? "enabled" : "disabled";
    const mess = await store.setProject(project);
    checkNoError(mess);
  };
  
  return (
  <Popover key={1}
           isOpen={open}
           onOpenChange={setOpen}
           placement="bottom-end"
           classNames={{
             content: "rounded-none",
           }}>
    <PopoverTrigger>
      <div className="h-full w-full text-right">
        <span className="material-icons cursor-pointer !text-[16px] !text-black align-middle">
          more_vert
        </span>
      </div>
    </PopoverTrigger>
    <PopoverContent className="p-0">
      <FlexRow flex={0}
               direction="column"
               align="start"
               className="bg-white text-black min-w-[205px] py-[8px] gap-[8px]">
        {(role.admin || role?.privilages?.includes("EditProjectSettings")) &&
            <MenuItem icon="edit_note"
              label={t('projects:edit_project')}
              onClick={handleEdit} />
        }
        {(role.admin || role?.privilages?.includes("CreateProjects")) &&
            <MenuItem icon="content_copy"
              label={t('projects:clone_project')}
              onClick={handleClone} />
        }
      {role.admin &&
        <MenuItem icon={props.data.state === "disabled" ? "public" : "public_off"}
                  label={t(props.data.state === "disabled"
                             ? 'projects:reactivate_project'
                             : 'projects:deactivate_project')}
                  onClick={handleToggleState} />
      }
      {(role.admin || role?.privilages?.includes("DeleteProjects"))&&
        <MenuItem icon="delete"
                  color="!text-[--Warning]"
                  label={t('projects:delete_project:caption')}
                  onClick={handleDelete} />
      }
      </FlexRow>
    </PopoverContent>
  </Popover>
  );
};

export default Projects;
